import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Zap,
  Users,
  Crown,
  ChevronDown,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { Button } from "../components/ui";
import { ScrollReveal } from "../components/effects";

const plans = [
  {
    name: "免费版",
    price: "0",
    period: "/月",
    description: "适合个人用户体验",
    icon: Zap,
    features: [
      "每月 5,000 字处理额度",
      "3 种基础写作风格",
      "标准去AI化模式",
      "7 天历史记录",
    ],
    cta: "免费开始",
    variant: "secondary" as const,
    popular: false,
  },
  {
    name: "专业版",
    price: "29",
    period: "/月",
    description: "适合内容创作者和专业人士",
    icon: Crown,
    features: [
      "每月 50,000 字处理额度",
      "全部 6 种写作风格",
      "高级去AI化模式",
      "无限历史记录",
      "优先处理队列",
      "API 访问权限",
    ],
    cta: "立即升级",
    variant: "primary" as const,
    popular: true,
  },
  {
    name: "团队版",
    price: "99",
    period: "/月",
    description: "适合企业和团队",
    icon: Users,
    features: [
      "每月 200,000 字处理额度",
      "全部功能 + 团队协作",
      "自定义风格训练",
      "专属客服支持",
      "SSO 单点登录",
      "私有化部署选项",
    ],
    cta: "联系销售",
    variant: "secondary" as const,
    popular: false,
  },
];

const faqs = [
  {
    q: "免费版有字数限制吗？",
    a: "免费版每月提供 5,000 字的处理额度，适合轻度用户。如果超出额度，可以等待次月重置或升级至专业版。",
  },
  {
    q: "可以随时取消订阅吗？",
    a: "当然可以。您可以随时在账户设置中取消订阅，取消后仍可使用至当前计费周期结束。",
  },
  {
    q: "团队版支持多少成员？",
    a: "团队版默认支持最多 10 名成员。如需更多成员名额，请联系我们的销售团队获取定制方案。",
  },
  {
    q: "支持哪些支付方式？",
    a: "我们支持支付宝、微信支付以及主流信用卡。企业客户还可以选择对公转账。",
  },
  {
    q: "如何保证数据安全？",
    a: "所有数据传输均采用 TLS 加密，我们不会将您的内容用于模型训练。专业版和团队版额外提供数据隔离和审计日志功能。",
  },
];

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="border border-white/10 rounded-2xl bg-white/[0.03] backdrop-blur-md overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="text-lg font-medium text-white">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-5 h-5 text-white/60" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-white/70 leading-relaxed">{answer}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export const PricingPage = () => {
  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#6366f1]/10 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              选择适合你的<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7]">方案</span>
            </h1>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              从免费开始，随时升级。找到最适合你创作需求的计划。
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  whileHover={{ y: -8 }}
                  className={`relative rounded-3xl p-8 ${
                    plan.popular
                      ? "bg-gradient-to-b from-white/[0.08] to-white/[0.03] border-2 border-[#6366f1]/50"
                      : "bg-white/[0.03] border border-white/10"
                  } backdrop-blur-xl`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white text-sm font-medium">
                      最受欢迎
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#818cf8]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-1">{plan.name}</h3>
                    <p className="text-white/50 text-sm">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">¥{plan.price}</span>
                    <span className="text-white/50">{plan.period}</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-white/70 text-sm">
                        <Check className="w-5 h-5 text-[#6366f1] shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={plan.variant}
                    size="lg"
                    className={`w-full ${plan.popular ? "bg-gradient-to-r from-[#6366f1] to-[#a855f7] hover:opacity-90" : ""}`}
                  >
                    {plan.cta}
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12">
              常见<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7]">问题</span>
            </h2>
          </ScrollReveal>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.q} answer={faq.a} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-12 rounded-3xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/10 backdrop-blur-xl"
          >
            <MessageCircle className="w-12 h-12 text-[#6366f1] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">还有疑问？</h2>
            <p className="text-white/60 mb-8 max-w-lg mx-auto">
              联系我们的团队获取定制方案，我们将为你提供最合适的建议。
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-[#6366f1] to-[#a855f7] hover:opacity-90"
              rightIcon={<ArrowRight className="w-5 h-5" />}
            >
              联系销售团队
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
